import { useEffect, useRef } from "react";
import { useUserpilot } from "@platform/hooks/useUserpilot";

import useEmailStore from "@store/useEmailStore";
import useDialogStore from "@store/useDialogStore";
import useSystemStore from "@store/useSystemStore";
import usePlatformStore from "@store/usePlatformStore";
import useUserStore from "@store/useUserStore";
import { useGetEmail } from "@screens/sync-email/hooks/useGetEmail";
import { useGetUser } from "@screens/connect-meridian/hooks/useGetUser";
import { useGetThread } from "@screens/sync-email/hooks/useGetThread";
import { useSyncEmail } from "@screens/sync-email/hooks/useSyncEmail";
import { ActiveScreen } from "@enums/screens.enum";
import { ItemType } from "@enums/common.enum";

const UI_TRANSITION_DELAY_SHORT_MS = 800;

export function useEmailInitialization(hasSession: boolean) {
  const { track } = useUserpilot();
  const { isUserConnected, isPending: isUserPending } = useUserStore();
  const { itemType, itemId, isComposeMode } = usePlatformStore();
  const lastItemId = useRef<string | undefined>(undefined);
  const { isAppLoading, setIsAppLoading, changeActiveScreen } = useSystemStore();
  const { closeDialog } = useDialogStore();
  const { setEmailId, setThreadId } = useEmailStore();
  const { data: user, error: userError } = useGetUser(false);
  const { error: threadError } = useGetThread(false);
  const { mutate: syncEmailMutate, error: syncEmailError } = useSyncEmail();

  const isAutoSynced = useRef(false);
  const isManualSyncRequired = user?.organization?.manual_sync_required ?? false;
  const autoSubscribeEmailThreads =
    user?.organization?.settings?.emails_thread_auto_subscribe ?? false;

  const { data: email, error: getEmailError, isLoading } = useGetEmail(isManualSyncRequired);

  const hasEmailError = !email?.id || !!getEmailError || !!syncEmailError || !!threadError;

  useEffect(() => {
    // Preload the initial screen when the add-in is loaded and user is connected
    if (!isUserConnected || itemType !== ItemType.EMAIL) return;

    if (isLoading) {
      changeActiveScreen(ActiveScreen.SYNC_EMAIL);
      return;
    }

    changeActiveScreen(hasEmailError ? ActiveScreen.SYNC_EMAIL : ActiveScreen.THREAD);
  }, [isUserConnected, hasEmailError, isLoading, isManualSyncRequired]);

  useEffect(() => {
    if (!isManualSyncRequired && isUserConnected && !isAutoSynced.current && itemId) {
      // Preload the "Thread" screen
      changeActiveScreen(ActiveScreen.THREAD);

      // Autosync current email
      syncEmailMutate(
        {
          identifier: itemId,
          sync_thread: autoSubscribeEmailThreads,
          is_draft: isComposeMode,
        },
        {
          onSuccess: () => {
            isAutoSynced.current = true;
          },
        }
      );
    }
  }, [isManualSyncRequired, isUserConnected, isAutoSynced.current, itemId]);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;

    if (hasSession && !isUserPending) {
      // Small delay to prevent flickering between screens
      timer = setTimeout(() => setIsAppLoading(false), UI_TRANSITION_DELAY_SHORT_MS);
    }

    if (!!userError || (!isUserConnected && !isUserPending)) {
      closeDialog();
      changeActiveScreen(ActiveScreen.CONNECT);
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [isAppLoading, hasSession, userError, isUserConnected, isUserPending]);

  // Reset store and refs when the user switches to a different item,
  // so the new item gets a fresh sync instead of showing stale data.
  // Triggered by Chrome (URL/selection change) and Outlook (pinned mode ItemChanged).
  useEffect(() => {
    if (!itemId) return;

    if (itemId !== lastItemId.current) {
      lastItemId.current = itemId;
      isAutoSynced.current = false;
      // Restore ids from the (cached) email query keyed on itemId: a revisited email shows
      // its data immediately, while a never-seen email has no cache so this is null and
      // autosync populates it. Avoids both the stale-data and the blank-on-revisit cases.
      setEmailId(email?.id ?? null);
      setThreadId(email?.thread ?? null);
    }
  }, [itemId, setEmailId, setThreadId]);

  useEffect(() => {
    track("Click: Open Add-in from Emails");
  }, []);
}
